/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Loader2, Copy } from 'lucide-react';
import { suggestIcd10 } from '@/lib/ai_clinical';
import { cn } from '@/lib/utils';

/**
 * Icd10Suggester — inline component next to clinical note textarea.
 * Suggests ICD-10 codes with confidence and rationale.
 */
export const Icd10Suggester = ({ noteText, apptId, onPickCode }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!noteText || noteText.trim().length < 5) {
      toast.error('Scrivi una nota clinica prima');
      return;
    }
    setLoading(true);
    try {
      const res = await suggestIcd10(noteText, apptId);
      setResult(res.result);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore ICD-10');
    } finally {
      setLoading(false);
    }
  };

  const CodeRow = ({ c, primary }) => (
    <div className="flex items-start gap-2 p-2 rounded-lg border border-border/60 hover:bg-accent/40 transition-colors" data-testid={`icd10-${c.code}`}>
      <Badge variant="outline" className={cn('font-mono flex-shrink-0', primary ? 'bg-primary/10 text-primary border-primary/30' : '')}>{c.code}</Badge>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium">{c.description_it}</div>
        {c.rationale && <div className="text-[10px] text-muted-foreground mt-0.5">{c.rationale}</div>}
      </div>
      <div className="text-[10px] font-mono text-muted-foreground flex-shrink-0">{Math.round((c.confidence || 0) * 100)}%</div>
      {onPickCode && (
        <Button variant="ghost" size="icon" className="h-6 w-6 flex-shrink-0" onClick={() => onPickCode(c)} aria-label="Copia codice"><Copy className="h-3 w-3" /></Button>
      )}
    </div>
  );

  return (
    <div className="space-y-2" data-testid="icd10-suggester">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Suggerimenti ICD-10 AI</div>
        <Button size="sm" variant="ghost" onClick={run} disabled={loading} data-testid="icd10-run-btn">
          {loading ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> AI…</> : <><Sparkles className="h-3.5 w-3.5" /> Suggerisci</>}
        </Button>
      </div>
      <AnimatePresence>
        {result && (
          <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className="space-y-1.5">
            {result.primary_codes?.length > 0 && (
              <>
                <div className="text-[10px] uppercase tracking-wider font-bold text-primary">Principali</div>
                {result.primary_codes.map((c) => <CodeRow key={`p-${c.code}`} c={c} primary />)}
              </>
            )}
            {result.secondary_codes?.length > 0 && (
              <>
                <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mt-2">Secondari</div>
                {result.secondary_codes.map((c) => <CodeRow key={`s-${c.code}`} c={c} />)}
              </>
            )}
            {result.notes && <div className="text-[10px] text-muted-foreground italic mt-1">{result.notes}</div>}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
